import {
    VenueAnalysisDataTimeSeriesItem,
    VenueAnalysisDataState,
    VenueAnalysisFillInfoState,
    VenueAnalysisOrderInfoState,
} from './store/stream/VenueAnalysisStreamDataState';
import values from 'lodash/values';
import { safeCalculatePercent } from 'utils/percent';
import {
    MarketVolItem,
    MarketVolMap,
} from 'widgets/venueAnalysis/api/VenueAnalysisResponseData';
import {
    getVenueAnalysisLiquidity,
    VenueAnalysisLiquidity,
} from 'widgets/venueAnalysis/venueAnalysisLiquidity';
import sum from 'lodash/sum';
import {
    isVenueSelected,
    VenueAnalysisSelection,
} from 'widgets/venueAnalysis/store/component/VenueAnalysisSelection';
import { HighlightedVenue } from 'widgets/venueAnalysis/highlighting';

export interface VenueAnalysisData {
    orderVenue?: VenueAnalysisOrderVenueAggregatedData;
    orderLiquidity?: VenueAnalysisAggregatedLiquidityItem[];
    mktVolHist?: VenueAnalysisAggregatedData;
    mktVolRealTime?: VenueAnalysisAggregatedData;
    fillInfo?: VenueAnalysisFillInfoState;
    orderInfo?: VenueAnalysisOrderInfoState;
}

export interface VenueAnalysisAggregatedData {
    venueTypes: VenueAnalysisVenueTypeData[];
    qty: number;
}

export interface VenueAnalysisOrderVenueAggregatedData
    extends VenueAnalysisAggregatedData {
    orderId: number;
}

export interface VenueAnalysisAggregatedLiquidityItem {
    liquidity: VenueAnalysisLiquidity;
    qty: number;
    percent: number;
}

export interface VenueAnalysisAggregatedDataItem {
    name: string;
    qty: number;
    percent: number;
    highlightItem?: HighlightedVenue;
}

export interface VenueAnalysisVenueTypeData
    extends VenueAnalysisAggregatedDataItem {
    venues: VenueAnalysisVenueData[];
}

export type VenueAnalysisVenueData = VenueAnalysisAggregatedDataItem & {
    venueTypeName?: string;
    micCode: string;
};

interface VenueAggregatedDataDictionary {
    [venue: string]: VenueAnalysisVenueData;
}
interface VenueTypeAggregatedDataDictionaryItem
    extends VenueAnalysisAggregatedDataItem {
    venues: VenueAggregatedDataDictionary;
}
interface VenueTypeAggregatedDataDictionary {
    [venueType: string]: VenueTypeAggregatedDataDictionaryItem;
}

export function getVenueAnalysisData(
    dataState: VenueAnalysisDataState | undefined,
    selection?: VenueAnalysisSelection,
    includePreviousDaysFills?: boolean
): VenueAnalysisData | undefined {
    if (!dataState) {
        return undefined;
    }

    const ret: VenueAnalysisData = {};

    if (dataState.timeSeries && dataState.timeSeries.length > 0) {
        aggregateTimeSeries(
            ret,
            dataState.timeSeries,
            selection,
            includePreviousDaysFills
        );
    }

    if (dataState.mktVolHist) {
        ret.mktVolHist = aggregateMarketVolumeMap(dataState.mktVolHist);
    }

    if (dataState.mktVolRt) {
        ret.mktVolRealTime = aggregateMarketVolumeMap(dataState.mktVolRt);
    }

    ret.fillInfo = dataState.fillInfo ?? undefined;
    ret.orderInfo = dataState.orderInfo ?? undefined;

    return ret;
}

function aggregateTimeSeries(
    data: VenueAnalysisData,
    timeSeries: VenueAnalysisDataTimeSeriesItem[],
    selection?: VenueAnalysisSelection,
    includePreviousDaysFills?: boolean
): void {
    const orderVenues: VenueTypeAggregatedDataDictionary = {};
    const orderLiquidity: VenueAnalysisAggregatedLiquidityItem[] = [];
    const orderId = timeSeries[0].orderId;

    const PreviousSessionVenueName = 'Previous Session Fills';

    const filteredTimeSeries = includePreviousDaysFills
        ? timeSeries
        : timeSeries.filter((t) => t.venueName !== PreviousSessionVenueName);

    for (const timeSeriesItem of filteredTimeSeries) {
        aggregateOrdersByVenueType(orderVenues, timeSeriesItem);
        aggregateOrdersByLiquidity(orderLiquidity, timeSeriesItem, selection);
    }

    data.orderVenue = {
        ...toAggregatedData(orderVenues),
        orderId: orderId,
    };

    data.orderLiquidity = orderLiquidity.filter((o) => !!o);
    calculateLiquidityPercentages(data.orderLiquidity);
}

function aggregateOrdersByVenueType(
    venueTypeDictionary: VenueTypeAggregatedDataDictionary,
    item: VenueAnalysisDataTimeSeriesItem
) {
    const venueTypeName = item.venueType ?? 'None';
    const venueName = item.venueName ?? 'None';
    const micCode = item.lastMkt
        ? item.lastMkt
        : item.executionBroker
        ? item.executionBroker
        : 'None';

    if (!venueTypeDictionary[venueTypeName]) {
        venueTypeDictionary[venueTypeName] = {
            venues: {},
            name: venueTypeName,
            qty: 0,
            percent: 0,
            highlightItem: { venueTypeName },
        };
    }
    const venueType = venueTypeDictionary[venueTypeName];

    if (!venueType.venues[venueName]) {
        venueType.venues[venueName] = {
            name: venueName,
            micCode,
            percent: 0,
            highlightItem: { venueTypeName, venueName },
            qty: 0,
        };
    }

    venueType.qty += item.fillQty;
    venueType.venues[venueName].qty += item.fillQty;
}

function aggregateOrdersByLiquidity(
    liquidityDictionary: VenueAnalysisAggregatedLiquidityItem[],
    item: VenueAnalysisDataTimeSeriesItem,
    selection?: VenueAnalysisSelection
) {
    if (
        selection?.venueTypeName &&
        item.venueType !== selection.venueTypeName
    ) {
        return;
    }

    if (isVenueSelected(selection) && item.venueName !== selection.venueName) {
        return;
    }

    const liquidity = getVenueAnalysisLiquidity(item.liquidityCode);

    if (!liquidityDictionary[liquidity.index]) {
        liquidityDictionary[liquidity.index] = {
            liquidity,
            qty: 0,
            percent: 0,
        };
    }
    const aggregatedLiquidity = liquidityDictionary[liquidity.index];
    aggregatedLiquidity.qty += item.fillQty;
}

function aggregateMarketVolumeMap(
    map: MarketVolMap
): VenueAnalysisAggregatedData {
    const dict: VenueTypeAggregatedDataDictionary = {};

    Object.values(map).forEach((item) => {
        aggregateMarketVolItem(dict, item);
    });

    return toAggregatedData(dict);
}
function aggregateMarketVolItem(
    venueTypeDictionary: VenueTypeAggregatedDataDictionary,
    item: MarketVolItem
) {
    const venueTypeName = item.type ?? 'None';
    const venueName = item.name ?? 'None';
    const micCode = item.dest ?? 'None';

    if (!venueTypeDictionary[venueTypeName]) {
        venueTypeDictionary[venueTypeName] = {
            venues: {},
            name: venueTypeName,
            qty: 0,
            percent: 0,
            highlightItem: { venueTypeName },
        };
    }
    const venueType = venueTypeDictionary[venueTypeName];

    if (!venueType.venues[venueName]) {
        venueType.venues[venueName] = {
            name: venueName,
            micCode,
            qty: 0,
            percent: 0,
            highlightItem: { venueTypeName, venueName },
        };
    }

    venueType.qty += item.vol;
    venueType.venues[venueName].qty += item.vol;
}

function toAggregatedData(
    dict: VenueTypeAggregatedDataDictionary
): VenueAnalysisAggregatedData {
    const ret: VenueAnalysisAggregatedData = { venueTypes: [], qty: 0 };
    for (const venueTypeName of Object.keys(dict)) {
        const venueType = dict[venueTypeName];
        ret.qty += venueType.qty;
        ret.venueTypes.push({
            name: venueTypeName,
            qty: venueType.qty,
            venues: values(venueType.venues),
            percent: 0,
            highlightItem: { venueTypeName },
        });
    }
    calculatePercentages(ret);
    sortVenues(ret);
    return ret;
}

function sortVenues(aggregatedData: VenueAnalysisAggregatedData) {
    for (const venueType of aggregatedData.venueTypes) {
        venueType.venues.sort((a, b) => b.percent - a.percent);
    }
}

function calculatePercentages(aggregatedData: VenueAnalysisAggregatedData) {
    for (const venueType of aggregatedData.venueTypes) {
        venueType.percent = safeCalculatePercent(
            venueType.qty,
            aggregatedData.qty
        );
        for (const venue of venueType.venues) {
            venue.percent = safeCalculatePercent(venue.qty, venueType.qty);
        }
    }
}

function calculateLiquidityPercentages(
    liquidities: VenueAnalysisAggregatedLiquidityItem[]
) {
    const qty = sum(liquidities.map((l) => l.qty));
    for (const liquidity of liquidities) {
        liquidity.percent = safeCalculatePercent(liquidity.qty, qty);
    }
}
